import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// 共通モーダルコンポーネント。
// Portal / ESC・遮罩クリックで閉じる / 背景スクロールロック / 三段構成（固定ヘッダー・スクロール本体・固定フッター） /
// アクセシビリティ（role=dialog, aria-modal, aria-labelledby, 初期フォーカス・フォーカストラップ・フォーカス復元）を提供する。
// 既存の手書き fixed inset-0 モーダルはすべてこのコンポーネントへ統一する。
import { useEffect, useId, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes } from 'react-icons/fa';
const SIZE_CLASSES = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
    full: 'max-w-[95vw]',
};
// 背景スクロールロックの参照カウント（入れ子モーダルでも正しく解除する）
let openModalCount = 0;
// ロック前の body.overflow を保存して、解除時に元の値へ復元する
let previousBodyOverflow = '';
function lockBodyScroll() {
    openModalCount += 1;
    if (openModalCount === 1) {
        previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
    }
}
function unlockBodyScroll() {
    openModalCount = Math.max(0, openModalCount - 1);
    if (openModalCount === 0) {
        document.body.style.overflow = previousBodyOverflow;
    }
}
// ESC / フォーカストラップは最前面のモーダルだけが反応するためのスタック
const modalStack = [];
// ダイアログ内のフォーカス可能な要素を取得する
function getFocusableElements(container) {
    const selector = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(container.querySelectorAll(selector)).filter((el) => el.offsetParent !== null || el === document.activeElement);
}
const Modal = ({ isOpen, onClose, title, headerActions, footer, children, size = 'lg', closeOnBackdrop = true, closeOnEsc = true, hideCloseButton = false, bodyClassName = '', zIndexClassName = 'z-50', className = '', }) => {
    const dialogRef = useRef(null);
    // 安定した一意 ID（aria-labelledby とモーダルスタックの識別に使用）
    const reactId = useId();
    const titleId = `${reactId}-title`;
    // 背景スクロールロック
    useEffect(() => {
        if (!isOpen)
            return;
        lockBodyScroll();
        return () => unlockBodyScroll();
    }, [isOpen]);
    // 開いたら直前のフォーカス要素を覚え、ダイアログへフォーカスを移し、閉じたら元へ戻す
    useEffect(() => {
        if (!isOpen)
            return;
        const previouslyFocused = document.activeElement;
        dialogRef.current?.focus();
        return () => {
            previouslyFocused?.focus?.();
        };
    }, [isOpen]);
    // ESC で閉じる / Tab フォーカストラップ（最前面のモーダルのみ反応）
    useEffect(() => {
        if (!isOpen)
            return;
        const myId = reactId;
        modalStack.push(myId);
        const handleKeyDown = (e) => {
            if (modalStack.at(-1) !== myId)
                return;
            if (e.key === 'Escape' && closeOnEsc) {
                e.stopPropagation();
                onClose();
                return;
            }
            if (e.key === 'Tab') {
                const dialog = dialogRef.current;
                if (!dialog)
                    return;
                const focusables = getFocusableElements(dialog);
                if (focusables.length === 0) {
                    // フォーカス可能な要素が無ければダイアログ自身に留める
                    e.preventDefault();
                    dialog.focus();
                    return;
                }
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                const active = document.activeElement;
                if (e.shiftKey) {
                    if (active === first || active === dialog || !dialog.contains(active)) {
                        e.preventDefault();
                        last.focus();
                    }
                }
                else if (active === last || !dialog.contains(active)) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            const idx = modalStack.lastIndexOf(myId);
            if (idx !== -1)
                modalStack.splice(idx, 1);
        };
    }, [isOpen, closeOnEsc, onClose, reactId]);
    if (!isOpen)
        return null;
    const handleBackdropClick = () => {
        if (closeOnBackdrop)
            onClose();
    };
    const showHeader = title !== undefined || headerActions !== undefined || !hideCloseButton;
    const modalNode = (_jsxs("div", { className: `fixed inset-0 ${zIndexClassName} flex items-center justify-center p-4`, children: [_jsx("div", { className: "fixed inset-0 bg-gray-600 bg-opacity-50 transition-opacity", "aria-hidden": "true", onClick: handleBackdropClick }), _jsxs("div", { ref: dialogRef, role: "dialog", "aria-modal": "true", "aria-labelledby": title !== undefined ? titleId : undefined, tabIndex: -1, className: `relative flex flex-col w-full ${SIZE_CLASSES[size]} max-h-[90vh] bg-white rounded-lg shadow-xl overflow-hidden outline-none ${className}`, children: [showHeader && (_jsxs("div", { className: "flex items-center justify-between gap-3 px-6 py-4 border-b border-gray-200 flex-shrink-0", children: [_jsx("div", { id: titleId, className: "min-w-0 flex-1 text-lg font-semibold text-gray-900 truncate", children: title }), _jsxs("div", { className: "flex items-center gap-2 flex-shrink-0", children: [headerActions, !hideCloseButton && (_jsx("button", { type: "button", onClick: onClose, "aria-label": "\u9589\u3058\u308B", className: "text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded p-1", children: _jsx(FaTimes, {}) }))] })] })), _jsx("div", { className: `flex-1 min-h-0 overflow-y-auto px-6 py-4 ${bodyClassName}`, children: children }), footer !== undefined && (_jsx("div", { className: "flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex-shrink-0", children: footer }))] })] }));
    return ReactDOM.createPortal(modalNode, document.body);
};
export default Modal;
